/* global Highcharts:false */
/* global loadHighcharts:false */
/* global addScrollIntoViewListener:false */

/**
 * @param {string} title
 * @param {HTMLElement} domElement
 * @param {Object[]} slices
 * @param {string} slices[].color
 * @param {string} [slices[].description = '{label}: {value}%']
 * @param {string} slices[].label
 * @param {number} slices[].size
 * @param {Object} [options]
 * @param {boolean} [options.animated = false]
 * @param {string} [options.caption]
 * @param {boolean} [options.dark = false]
 * @param {string} [options.description]
 * @param {number} [options.height = 400]
 * @param {string} [options.title]
 * @param {number} [options.width]
 */
function createDonutChart(
	domElement,
	slices,
	options = {}
) {
	const {
		animated = true,
		caption,
		dark = false,
		description,
		height = 400,
		title,
		width,
	} = options;

	const config = {
		accessibility: {
			description,
			point: {
				descriptionFormatter: (point) => {
					const values = {
						value: point.options.y,
						label: point.name,
					};

					const template = slices.description || '{label}: {value}%';

					return template.replace(/{([^{}]+)}/g, (_, key) => {
						return values.hasOwnProperty(key) ? values[key] : '{key}';
					}).replace(/<br\/>/g, ' ');
				},
			},
			screenReaderSection: {
				beforeChartFormat: `
					<div>{typeDescription}</div>
					<div>{chartLongdesc}</div>
				`,
			},
			typeDescription: `Pie chart with ${slices.length} bars.`,
		},
		caption: {
			align: 'center',
			style: {
				color: dark ? '#fff' : '#12181f',
				fontSize: '14px',
				fontWeight: 400,
				lineHeight: '28px',
			},
			text: caption,
			useHTML: true,
			verticalAlign: 'bottom',
		},
		chart: {
			backgroundColor: dark ? '#3f0f45' : '#ecf0f3',
			events: {
				render: function () {
					this.legend.allItems.forEach((item) => {
						const textRect = item.legendItem.label.element.getBoundingClientRect();
						const verticalMiddle = textRect.height;

						item.legendItem.symbol.element.setAttribute('width', 39);
						item.legendItem.symbol.element.setAttribute('height', 22);
						item.legendItem.symbol.element.setAttribute(
							'y',
							4 + (verticalMiddle / 16) * 8
						);
					});
				},
			},
			height,
			spacingBottom: 20,
			spacingLeft: 20,
			spacingRight: 20,
			spacingTop: 23,
			type: 'pie',
			width,
		},
		credits: {
			enabled: false,
		},
		legend: {
			accessibility: {
				enabled: false,
			},
			align: 'right',
			borderColor: '#7C238E',
			borderRadius: 4,
			borderWidth: 2,
			enabled: true,
			itemHoverStyle: {
				color: dark ? '#fff' : '#12181f',
				cursor: 'auto',
			},
			itemMarginBottom: 12,
			itemMarginTop: 12,
			itemStyle: {
				color: dark ? '#fff' : '#12181f',
				fontSize: 13,
				fontWeight: 400,
				textOverflow: 'initial',
				width: 130,
			},
			layout: 'vertical',
			states: {
				hover: {
					enabled: false,
				},
			},
			symbolHeight: 22,
			symbolPadding: 16,
			symbolRadius: 2,
			symbolWidth: 39,
			verticalAlign: 'middle',
			width: 200,
		},
		plotOptions: {
			pie: {
				allowPointSelect: false,
				borderWidth: 0,
				dataLabels: {
					animation: animated ? {
						defer: 1000,
					} : false,
					align: 'center',
					color: dark ? '#fff' : '#12181f',
					connectorColor: dark ? '#fff' : '#12181f',
					connectorPadding: 0,
					connectorShape: function (labelPosition, connectorPosition) {
						const radius = (this.shapeArgs.innerR + this.shapeArgs.r) / 2;
						const angle = (this.shapeArgs.start + this.shapeArgs.end) / 2;

						const centroid = {
							x: this.shapeArgs.x + Math.cos(angle) * radius,
							y: this.shapeArgs.y + Math.sin(angle) * radius,
						};

						return [
							'M',
							centroid.x,
							centroid.y,
							'L',
							centroid.x,
							labelPosition.y + 4,
							'L',
							labelPosition.x,
							labelPosition.y + 4,
						];
					},
					connectorWidth: 2,
					distance: '15%',
					enabled: true,
					format: '{point.y:0f}%',
					style: {
						fontSize: 16,
						fontWeight: 700,
						textOutline: false,
					},
				},
				innerSize: '70%',
			},
			series: {
				point: {
					events: {
						legendItemClick: function () {
							return false
						},
					},
				},
				states: {
					hover: {
						enabled: false,
					},
					inactive: {
						opacity: 1,
					},
				},
			},
		},
		responsive: {
			rules: [
				{
					chartOptions: {
						chart: {
							height: height * 1.5,
							spacingRight: 20,
						},
						legend: {
							align: 'center',
							itemStyle: {
								paddingRight: 10,
								textOverflow: 'ellipsis',
								width: null,
							},
							layout: 'vertical',
							verticalAlign: 'bottom',
							width: '75%',
						},
					},
					condition: {
						maxWidth: 550,
					},
				},
			],
		},
		series: [
			{
				animation: animated ? {
					easing: (x) => 1 - Math.pow(1 - x, 3),
					duration: 1000,
				} : false,
				color: 'rgb(255 255 255 / 10%)',
				data: slices.map(slice => ({
					color: slice.color,
					name: slice.label,
					y: slice.size,
				})),
				showInLegend: true,
			},
		],
		title: {
			margin: 27,
			style: {
				color: dark ? '#fff' : '#12181f',
				fontSize: '20px',
				fontWeight: 400,
				lineHeight: '20px',
			},
			text: title,
		},
		tooltip: {
			enabled: false,
		},
	};

	let isInView = false;
	let isHighchartsReady = false;

	function render() {
		if (isInView && isHighchartsReady) {
			Highcharts.chart(domElement, config);
		}
	}

	addScrollIntoViewListener(domElement, () => {
		isInView = true;
		render();
	});

	loadHighcharts(() => {
		isHighchartsReady = true;
		render();
	});
}
